(() => {
  const initializedForms = new WeakSet();
  const requestVersions = new WeakMap();
  let catalogPromise = null;

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const getSuggestionLabel = (title) =>
    String(title || '')
      .replace(/\s*\([^)]*\)\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const damerauLevenshtein = (a, b) => {
    const rows = a.length + 1;
    const cols = b.length + 1;
    const matrix = Array.from({ length: rows }, () => Array(cols).fill(0));

    for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
    for (let j = 0; j < cols; j += 1) matrix[0][j] = j;

    for (let i = 1; i < rows; i += 1) {
      for (let j = 1; j < cols; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;

        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );

        if (
          i > 1 &&
          j > 1 &&
          a[i - 1] === b[j - 2] &&
          a[i - 2] === b[j - 1]
        ) {
          matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + cost);
        }
      }
    }

    return matrix[a.length][b.length];
  };

  const scoreSuggestion = (query, label) => {
    const normalizedQuery = normalize(query);
    const normalizedLabel = normalize(label);
    if (!normalizedQuery || !normalizedLabel) return 0;

    const queryCompact = normalizedQuery.replace(/\s+/g, '');
    const words = normalizedLabel.split(' ').filter((word) => word.length >= 3);
    const forms = new Set([normalizedLabel.replace(/\s+/g, ''), ...words]);

    if (words.length >= 2) forms.add(`${words[0]}${words[1]}`);

    let bestScore = 0;

    forms.forEach((form) => {
      if (!form) return;

      if (form === queryCompact) {
        bestScore = Math.max(bestScore, 1);
        return;
      }

      const distance = damerauLevenshtein(queryCompact, form);
      const longest = Math.max(queryCompact.length, form.length);
      let similarity = longest ? 1 - distance / longest : 0;
      const prefixMatch = form.startsWith(queryCompact) || queryCompact.startsWith(form);

      if (prefixMatch) similarity += 0.12;

      const maxDistance =
        queryCompact.length <= 4
          ? 1
          : queryCompact.length <= 7
            ? 2
            : queryCompact.length <= 12
              ? 3
              : Math.ceil(queryCompact.length * 0.25);

      const minSimilarity = queryCompact.length <= 4 ? 0.7 : 0.62;

      if ((distance <= maxDistance || prefixMatch) && similarity >= minSimilarity) {
        bestScore = Math.max(bestScore, Math.min(similarity, 1));
      }
    });

    return bestScore;
  };

  const getCatalog = () => {
    if (catalogPromise) return catalogPromise;

    const root = window.Shopify?.routes?.root || '/';
    const cleanRoot = root.endsWith('/') ? root : `${root}/`;
    const endpoints = [
      `${cleanRoot}products.json?limit=250`,
      `${cleanRoot}collections/all/products.json?limit=250`,
    ];

    catalogPromise = (async () => {
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) continue;

          const data = await response.json();
          if (Array.isArray(data.products)) return data.products;
        } catch (error) {
          // Fall through to the next storefront endpoint.
        }
      }

      return [];
    })();

    return catalogPromise;
  };

  const getSuggestions = async (query) => {
    const products = await getCatalog();
    const candidates = new Map();

    products.forEach((product) => {
      const label = getSuggestionLabel(product.title);
      if (!label) return;

      const key = normalize(label);
      if (!key || candidates.has(key)) return;

      const score = scoreSuggestion(query, label);
      if (score > 0) candidates.set(key, { label, score });
    });

    return Array.from(candidates.values())
      .sort((a, b) => b.score - a.score || a.label.length - b.label.length)
      .slice(0, 3);
  };

  const removeSuggestion = (form) => {
    form.querySelector('.search-spellcheck')?.remove();
  };

  const renderSuggestions = async (form) => {
    const input = form.querySelector('input[type="search"]');
    const emptyState = form.querySelector('.search__results .empty-state');
    if (!input || !emptyState || input.value.trim().length < 3) return;

    if (emptyState.querySelector('.search-spellcheck')) return;

    const query = input.value.trim();
    const version = (requestVersions.get(form) || 0) + 1;
    requestVersions.set(form, version);

    const suggestions = await getSuggestions(query);

    if (requestVersions.get(form) !== version || input.value.trim() !== query) return;
    if (!suggestions.length || !emptyState.isConnected) return;

    const language = (document.documentElement.lang || '').toLowerCase();
    const isDutch = language.startsWith('nl');

    const wrapper = document.createElement('div');
    wrapper.className = 'search-spellcheck';
    wrapper.setAttribute('aria-live', 'polite');

    const label = document.createElement('p');
    label.className = 'search-spellcheck__label';
    label.textContent = isDutch ? 'Bedoelde je:' : 'Did you mean:';

    const items = document.createElement('div');
    items.className = 'search-spellcheck__items';

    suggestions.forEach((suggestion) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'search-spellcheck__item';
      button.textContent = suggestion.label;
      button.setAttribute(
        'aria-label',
        isDutch ? `Zoek naar ${suggestion.label}` : `Search for ${suggestion.label}`
      );

      button.addEventListener('click', () => {
        input.value = suggestion.label;
        removeSuggestion(form);
        input.dispatchEvent(new Event('input', { bubbles: true }));

        try {
          input.focus({ preventScroll: true });
        } catch (error) {
          input.focus();
        }
      });

      items.appendChild(button);
    });

    wrapper.append(label, items);
    emptyState.appendChild(wrapper);
  };

  const setupForm = (form) => {
    if (initializedForms.has(form)) return;
    initializedForms.add(form);

    const input = form.querySelector('input[type="search"]');
    const results = form.querySelector('.search__results');
    if (!input || !results) return;

    input.addEventListener('input', () => {
      requestVersions.set(form, (requestVersions.get(form) || 0) + 1);
      removeSuggestion(form);
    });

    const observer = new MutationObserver(() => {
      if (form.querySelector('.search__results .empty-state')) {
        renderSuggestions(form);
      }
    });

    observer.observe(results, { childList: true, subtree: true });
    renderSuggestions(form);
  };

  const init = () => {
    document
      .querySelectorAll('search-drawer form.search__form')
      .forEach((form) => setupForm(form));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
